import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { SellerIntake, FormData, FieldConfig } from '../types';
import { sellerIntakeAPI } from '../services/api';
import {
  CONDITIONAL_HOA_FIELDS,
  isEmptyValue,
  isLogisticsRowComplete,
  isNo,
  isYes,
  LOGISTICS_SERVICES,
  parseUtilitiesTable,
} from '../lib/requiredFields';

interface FormState {
  intake: SellerIntake | null;
  formData: FormData;
  requiredFields: FieldConfig[];
  isLoading: boolean;
  error: string | null;
  currentSection: number;
  isDirty: boolean;
}

type FormAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_INTAKE'; payload: SellerIntake }
  | { type: 'SET_FORM_DATA'; payload: FormData }
  | { type: 'SET_REQUIRED_FIELDS'; payload: FieldConfig[] }
  | { type: 'UPDATE_SECTION'; payload: { section: string; data: any } }
  | { type: 'SET_CURRENT_SECTION'; payload: number }
  | { type: 'SET_DIRTY'; payload: boolean }
  | { type: 'UPDATE_PROGRESS'; payload: { section: number; completion: number } };

const initialState: FormState = {
  intake: null,
  formData: {},
  requiredFields: [],
  isLoading: false,
  error: null,
  currentSection: 1,
  isDirty: false,
};

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_INTAKE':
      return { ...state, intake: action.payload };
    case 'SET_FORM_DATA':
      return { ...state, formData: action.payload, isDirty: false };
    case 'SET_REQUIRED_FIELDS':
      return { ...state, requiredFields: action.payload };
    case 'UPDATE_SECTION':
      return {
        ...state,
        formData: { ...state.formData, [action.payload.section]: action.payload.data },
        isDirty: true,
      };
    case 'SET_CURRENT_SECTION':
      return { ...state, currentSection: action.payload };
    case 'SET_DIRTY':
      return { ...state, isDirty: action.payload };
    case 'UPDATE_PROGRESS':
      return {
        ...state,
        currentSection: action.payload.section,
        intake: state.intake ? {
          ...state.intake,
          current_section: action.payload.section,
          completion_percentage: action.payload.completion,
          status: action.payload.completion === 100 ? 'submitted' : 'in_progress'
        } : null,
      };
    default:
      return state;
  }
}

interface FormContextType {
  state: FormState;
  createIntake: (propertyAddress: string, ownerCount: number) => Promise<void>;
  loadIntake: (accessToken: string) => Promise<void>;
  saveSection: (section: string, data: any) => Promise<void>;
  saveNow: () => Promise<void>;
  updateIntakeInfo: (fields: { property_address?: string; owner_count?: number }) => Promise<void>;
  sendSaveLink: () => Promise<{ magic_link: string; email_sent: boolean; seller_email: string | null } | null>;
  updateProgress: (section: number, completion: number) => Promise<void>;
  goToSection: (section: number) => void;
  nextSection: () => void;
  previousSection: () => void;
  calculateCompletion: () => number;
  getSubmitBlockers: () => string[];
  submitForm: () => Promise<void>;
}

const FormContext = createContext<FormContextType | undefined>(undefined);

export function FormProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(formReducer, initialState);

  // Auto-save effect
  useEffect(() => {
    if (state.isDirty && state.intake) {
      const intake = state.intake;
      const formData = state.formData;
      const timer = setTimeout(async () => {
        // Call the API directly — do NOT call saveSection() here because that dispatches
        // UPDATE_SECTION which sets isDirty:true and re-triggers this effect (infinite loop)
        for (const [section, data] of Object.entries(formData)) {
          if (data) {
            try {
              await sellerIntakeAPI.saveSection(intake.access_token, section, data);
            } catch (err) {
              console.error('Auto-save error:', err);
            }
          }
        }
        dispatch({ type: 'SET_DIRTY', payload: false });
      }, 2000); // Auto-save after 2 seconds of inactivity

      return () => clearTimeout(timer);
    }
  }, [state.isDirty, state.formData, state.intake]);

  const loadRequiredFields = async () => {
    try {
      const requiredFields = await sellerIntakeAPI.getRequiredFields();
      dispatch({ type: 'SET_REQUIRED_FIELDS', payload: requiredFields });
    } catch (error) {
      console.error('Failed to load required fields config:', error);
    }
  };

  const createIntake = async (propertyAddress: string, ownerCount: number) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const intake = await sellerIntakeAPI.createIntake(propertyAddress, ownerCount);
      dispatch({ type: 'SET_INTAKE', payload: intake });
      dispatch({ type: 'SET_CURRENT_SECTION', payload: 1 });
      await loadRequiredFields();
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to create intake' });
      console.error('Error creating intake:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const loadIntake = async (accessToken: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const [intake, formData] = await Promise.all([
        sellerIntakeAPI.getIntake(accessToken),
        sellerIntakeAPI.getAllData(accessToken),
      ]);

      dispatch({ type: 'SET_INTAKE', payload: intake });
      dispatch({ type: 'SET_FORM_DATA', payload: formData });
      dispatch({ type: 'SET_CURRENT_SECTION', payload: intake.current_section });
      await loadRequiredFields();
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load intake' });
      console.error('Error loading intake:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const saveSection = async (section: string, data: any) => {
    if (!state.intake) return;

    // Update formData immediately so Save & Exit can read owner email before API round-trip
    dispatch({ type: 'UPDATE_SECTION', payload: { section, data } });

    try {
      await sellerIntakeAPI.saveSection(state.intake.access_token, section, data);
    } catch (error) {
      console.error('Error saving section:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to save section' });
    }
  };

  const updateIntakeInfo = async (fields: { property_address?: string; owner_count?: number }) => {
    if (!state.intake) return;
    try {
      await sellerIntakeAPI.updateIntake(state.intake.access_token, fields as any);
      dispatch({ type: 'SET_INTAKE', payload: { ...state.intake, ...fields } });
    } catch (error) {
      console.error('Error updating intake info:', error);
    }
  };

  const saveNow = async () => {
    if (!state.intake || !state.isDirty) return;

    const intake = state.intake;
    const formData = state.formData;
    for (const [section, data] of Object.entries(formData)) {
      if (data) {
        await sellerIntakeAPI.saveSection(intake.access_token, section, data);
      }
    }
    dispatch({ type: 'SET_DIRTY', payload: false });
  };

  const sendSaveLink = async (): Promise<{ magic_link: string; email_sent: boolean; seller_email: string | null } | null> => {
    if (!state.intake) return null;
    try {
      if (state.isDirty) {
        await saveNow();
      }

      // Flush Section 1 owner email before requesting resume link
      const owners = state.formData.owners;
      if (Array.isArray(owners) && owners.length > 0) {
        const intakeOwners = owners.map((o: { owner_number?: number }, i: number) => ({
          ...o,
          intake_id: state.intake!.id,
          owner_number: o.owner_number ?? i + 1,
        }));
        await sellerIntakeAPI.saveSection(state.intake.access_token, 'owners', intakeOwners);
      }

      const primaryOwner = Array.isArray(owners) ? owners[0] : null;
      const response = await fetch(`/api/intakes/${state.intake.access_token}/send-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          seller_email: primaryOwner?.email || undefined,
          seller_name: primaryOwner?.full_name || undefined,
        }),
      });
      const json = await response.json();
      if (!json.magic_link) return null;
      return {
        magic_link: json.magic_link,
        email_sent: !!json.email_sent,
        seller_email: json.seller_email ?? null,
      };
    } catch (error) {
      console.error('Error sending save link:', error);
      return null;
    }
  };

  const updateProgress = async (section: number, completion: number) => {
    if (!state.intake) return;

    try {
      await sellerIntakeAPI.updateIntake(state.intake.access_token, {
        current_section: section,
        completion_percentage: completion,
        status: completion === 100 ? 'submitted' : 'in_progress',
      });
      dispatch({ type: 'UPDATE_PROGRESS', payload: { section, completion } });
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const goToSection = (section: number) => {
    dispatch({ type: 'SET_CURRENT_SECTION', payload: section });
  };

  const nextSection = () => {
    if (state.currentSection < 9) {
      goToSection(state.currentSection + 1);
    }
  };

  const previousSection = () => {
    if (state.currentSection > 1) {
      goToSection(state.currentSection - 1);
    }
  };

  const calculateCompletion = (): number => {
    const sections = [
      'owners',
      'communication_preferences',
      'selling_plans',
      'showing_info',
      'property_basics',
      'interior_utilities',
      'home_features',
      'hoa_info',
      'renovations_systems',
    ];

    const completedSections = sections.filter(section => {
      const data = (state.formData as any)[section];
      return data && Object.keys(data).some(key => {
        const value = data[key];
        return value !== undefined && value !== null && value !== '';
      });
    });

    return Math.round((completedSections.length / sections.length) * 100);
  };

  const getSubmitBlockers = (): string[] => {
    const blockers: string[] = [];
    const ownerCount = state.intake?.owner_count ?? 1;
    const owners: any[] = Array.isArray((state.formData as any).owners)
      ? (state.formData as any).owners
      : [];
    const hoaConditional = new Set<string>(CONDITIONAL_HOA_FIELDS);

    for (const field of state.requiredFields) {
      const { section, field_key, field_label, section_label } = field;
      const label = `${field_label} (${section_label})`;

      if (section === 'seller_intakes') {
        const value = (state.intake as any)?.[field_key];
        if (isEmptyValue(value)) blockers.push(label);

      } else if (section === 'owners') {
        for (let i = 0; i < ownerCount; i++) {
          const value = owners[i]?.[field_key];
          if (isEmptyValue(value)) {
            const ownerLabel = ownerCount > 1 ? ` — Owner ${i + 1}` : '';
            blockers.push(`${field_label}${ownerLabel} (${section_label})`);
          }
        }

      } else if (section === 'property_basics' && field_key === 'roof_year_replaced') {
        const pb = (state.formData as any).property_basics;
        // Only required when seller says the roof is not original
        if (isNo(pb?.roof_original) && isEmptyValue(pb?.roof_year_replaced)) {
          blockers.push(label);
        }

      } else if (section === 'hoa_info' && hoaConditional.has(field_key)) {
        const hoa = (state.formData as any).hoa_info;
        if (isYes(hoa?.has_hoa) && isEmptyValue(hoa?.[field_key])) {
          blockers.push(label);
        }

      } else if (section === 'renovations_systems' && field_key === 'utilities_logistics') {
        const rs = (state.formData as any).renovations_systems;
        const table = parseUtilitiesTable(rs);
        const incomplete = LOGISTICS_SERVICES.filter(
          (service) => !isLogisticsRowComplete(table[service])
        );
        if (incomplete.length > 0) {
          blockers.push(
            `System & House Logistics — complete or mark N/A for: ${incomplete.join(', ')} (${section_label})`
          );
        }

      } else {
        const value = (state.formData as any)[section]?.[field_key];
        if (isEmptyValue(value)) blockers.push(label);
      }
    }

    return blockers;
  };

  const submitForm = async () => {
    if (!state.intake) return;

    try {
      // Save any remaining dirty data
      if (state.isDirty) {
        Object.entries(state.formData).forEach(([section, data]) => {
          if (data) {
            saveSection(section, data);
          }
        });
      }

      // Update status to submitted
      await updateProgress(state.currentSection, 100);
    } catch (error) {
      console.error('Error submitting form:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to submit form' });
    }
  };

  const value: FormContextType = {
    state,
    createIntake,
    loadIntake,
    saveSection,
    saveNow,
    updateIntakeInfo,
    sendSaveLink,
    updateProgress,
    goToSection,
    nextSection,
    previousSection,
    calculateCompletion,
    getSubmitBlockers,
    submitForm,
  };

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
}

export function useFormContext() {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
}
